import { useMutation } from "@vue/apollo-composable";
import { gqlOutcome } from "@/api/client";
import { BankTransferDocument, BpayPaymentDocument, PayIdPaymentDocument, TransferStatus } from "@/api/generated/graphql";

export enum PaymentType {
  Transfer = "transfer",
  BankTransfer = "bank-transfer",
  BPAY = "bpay",
  PayID = "pay-id",
}

export type PaymentResult = {
  id: string;
  dateTime: Date;
  outcome: TransferStatus;
};

export async function makeBankTransferPayment({
  name,
  accountNumber,
  bsb,

  fromAccountId,
  amount,
  description,
}: {
  name: string;
  accountNumber: string;
  bsb: string;

  fromAccountId: string;
  amount: number;
  description: string;
}): Promise<PaymentResult> {
  const { onDone, onError, mutate } = useMutation(BankTransferDocument);
  const resFuture = new Promise<PaymentResult>((resolve, reject) => {
    onDone(result => {
      const transferResult = result.data?.bankTransfer;
      if (transferResult?.outcome !== gqlOutcome.ACCEPTED) {
        reject(new Error(transferResult?.outcome ?? "No outcome"));
      }

      resolve({
        id: transferResult!.id!,
        dateTime: transferResult!.dateTime!,
        outcome: transferResult!.outcome!,
      });
    });
    onError(error => {
      reject(error);
    });
  });

  await mutate({
    input: {
      name: name,
      accountNumber: accountNumber,
      bsbNumber: bsb,

      fromAccount: fromAccountId,
      amount,
      description: description,
    },
  });

  return resFuture;
}

export async function makeBPayPayment({
  name,
  billerCode,
  reference,

  fromAccountId,
  amount,
  description,
}: {
  name: string;
  billerCode: string;
  reference: string;

  fromAccountId: string;
  amount: number;
  description: string;
}): Promise<PaymentResult> {
  const { onDone, onError, mutate } = useMutation(BpayPaymentDocument);
  const resFuture = new Promise<PaymentResult>((resolve, reject) => {
    onDone(result => {
      const bpayResult = result.data?.bpayPayment;
      if (bpayResult?.outcome !== gqlOutcome.ACCEPTED) {
        reject(new Error(bpayResult?.outcome ?? "No outcome"));
      }

      resolve({
        id: bpayResult!.id!,
        dateTime: bpayResult!.dateTime!,
        outcome: bpayResult!.outcome!,
      });
    });
    onError(error => {
      reject(error);
    });
  });

  await mutate({
    input: {
      billerName: name,
      billerCode: billerCode,
      reference: reference,

      fromAccount: fromAccountId,
      amount,
      description: description,
    },
  });

  return resFuture;
}

export async function makePayIdPayment({
  name,
  fromAccountId,
  payId, // the payID identifier
  amount,
  description,
}: {
  name: string;
  fromAccountId: string;
  payId: string;
  amount: number;
  description: string;
}): Promise<PaymentResult> {
  const { onDone, onError, mutate } = useMutation(PayIdPaymentDocument);
  const resFuture = new Promise<PaymentResult>((resolve, reject) => {
    onDone(result => {
      const payIdResult = result.data?.payIdPayment;
      if (payIdResult?.outcome !== gqlOutcome.ACCEPTED) {
        reject(new Error(payIdResult?.outcome ?? "No outcome"));
      }

      resolve({
        id: payIdResult!.id!,
        dateTime: payIdResult!.dateTime!,
        outcome: payIdResult!.outcome!,
      });
    });
    onError(error => {
      reject(error);
    });
  });

  await mutate({
    input: {
      recipientName: name,
      fromAccount: fromAccountId,
      payId: payId,
      amount,
      description: description,
    },
  });

  return resFuture;
}
